<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useOrderStore, type Order, type OrderLine, OrderStatus } from '@/stores/orderStore';
import { useRouter } from 'vue-router';
import { useAuth } from '@clerk/vue';
import Layout from '../components/layout/Layout.vue';
import StellarBackground from '../components/animations/StellarBackground.vue';
import CyberLoading from '../components/ui/CyberLoading.vue';

const router = useRouter();
const { isSignedIn, isLoaded, getToken } = useAuth();
const orderStore = useOrderStore();

const isLoading = ref(true);
const productsLoaded = ref(false);
const selectedOrder = ref<Order | null>(null);
const isModalOpen = ref(false);
const products = ref<any[]>([]);
const user = ref({})
const isCrew = ref(false);

async function getData() {
  try {
    const response = await fetch('https://api.alterlan.be/v1/products');
    const userResponse = await fetch('https://api.alterlan.be/v1/users/me', {headers: {Authorization: `Bearer ${await getToken.value()}`}});
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    user.value = await userResponse.json();
    products.value = data;
    productsLoaded.value = true;
  } catch (error) {
    console.error('Error fetching products:', error);
    products.value = []; // Set empty array as fallback
  }
}

await getData().then(() => {
  console.log('Products loaded:', products.value);
  console.log('User loaded:', user.value);
  
  isCrew.value = user.value.roles.includes("CREW");
}).catch(error => {
  console.error('Failed to load products:', error);
});


async function getOrderStatus() {
  fetch('https://api.alterlan.be/v1/orders/open', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${await getToken.value()}`
    }
  }).then(response => {
    if (!response.ok) {
      throw new Error('Failed to get order status');
    }
    return response.json();
  }).then(data => {
    console.log("Data loaded:", data);
    
    if (data.length > 0) {
      // Process each order separately and combine only identical products within the same order
      const processedOrders = data.map((order: Order) => {
        const combinedOrderLines: OrderLine[] = [];
        
        order.orderLines.forEach((orderLine: OrderLine) => {
          const existingOrderLine = combinedOrderLines.find(
            (ol: OrderLine) => ol.product.id === orderLine.product.id && JSON.stringify(ol.supplements || []) === JSON.stringify(orderLine.supplements || [])
          );
          
          if (existingOrderLine) {
            existingOrderLine.quantity = Number(existingOrderLine.quantity) + 1;
          } else {
            combinedOrderLines.push({ 
              ...orderLine,
              quantity: 1
            });
          }
        });
        
        return {
          ...order,
          orderLines: combinedOrderLines
        };
      });

      orderStore.setOrders(processedOrders);
    }
  }).catch(error => {
    console.error('Error getting order status:', error);
  });
}

getOrderStatus();


const getProductName = (productId: string) => {
  if (!productsLoaded.value) {
    return 'Loading...';
  }
  const product = products.value.find(p => p.id === productId);
  if (!product) {
    console.error(`Product not found for ID: ${productId}`);
    return 'Product Not Found';
  }
  return product.name.en;
};

const getSupplements = (supplements: string[]) => {
  if (!supplements || supplements.length === 0) {
    return '';
  }
  let supplementString = ''
  
  supplements.forEach((supplement) => {
    supplementString += `(${supplement.name.en})`;
  });
  return supplementString;
};

const getProductCategory = (productId: string) => {
  const product = products.value.find(p => p.id === productId);
  return product ? product.category.name.en : 'Unknown Category';
};

const sortedCartItems = (orderLines: OrderLine[]) => {

  
  const beverages = orderLines.filter(item => {
    const product = products.value.find(p => p.id === item.product.id);
    return product && product.category.name.en === "Beverages";
  });
  
  const food = orderLines.filter(item => {
    const product = products.value.find(p => p.id === item.product.id);
    
    return product && product.category.name.en !=="Beverages";
  });
  
  console.log('Beverages:', beverages);
  console.log('Food:', food);
  return { beverages, food };
};

// Redirect to sign-in if not authenticated
const checkAuth = computed(() => {
  if (isLoaded.value && !isSignedIn.value) {
    setTimeout(() => {
      router.push({
      path: '/sign-in',
      query: { redirect: '/cateringQueue' }
    });
    }, 1200);
    return false;
  }
  setTimeout(() => {
    isLoading.value = false;
  }, 1200);
  return true;
});

const openOrderModal = (order: Order) => {

  selectedOrder.value = order;
  
  isModalOpen.value = true;
};

const closeModal = () => {
  selectedOrder.value = null;
  isModalOpen.value = false;
};

const updateOrderStatus = async (order: Order, newStatus: OrderStatus) => {
  if (order.status === OrderStatus.Pending && newStatus === OrderStatus.Preparing) {
    console.log('Order preparing:', order);
    await fetch(`https://api.alterlan.be/v1/orders/preparing/${order.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken.value()}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to update order status');
      }
      return response.json();
    }).then(data => {
      console.log('Order status updated successfully:', data);
      orderStore.updateOrderStatus(order.id, newStatus);
    }).catch(error => {
      console.error('Error updating order status:', error);
    });
  } else if (order.status === OrderStatus.Preparing && newStatus === OrderStatus.Completed) {
    console.log('Order completed:', order);
    await fetch(`https://api.alterlan.be/v1/orders/complete/${order.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken.value()}`
      },
      body: JSON.stringify({
        eventId: order.eventId,
        orderNumber: order.orderNumber
      })
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to complete order');
      }
      return response.json();
    }).then(data => {
      console.log('Order completed successfully:', data);
      orderStore.updateOrderStatus(order.id, newStatus);
      closeModal();
    }).catch(error => {
      console.error('Error completing order:', error);
    });
  } else if (order.status === OrderStatus.Completed && newStatus === OrderStatus.Preparing) {
    await fetch(`https://api.alterlan.be/v1/orders/preparing/${order.id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken.value()}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error('Failed to update order status');
      }
      return response.json();
    }).then(data => {
      console.log('Order status updated successfully:', data);
      orderStore.updateOrderStatus(order.id, newStatus);
    }).catch(error => {
      console.error('Error updating order status:', error);
    });
  } else {
    orderStore.updateOrderStatus(order.id, newStatus);
    closeModal();
  }
};

const toggleBeveragesCompleted = (order: Order) => {
  orderStore.toggleBeveragesCompleted(order.id);
};

const getWaitingTime = (order: Order) => {
  if (!order) return '00:00 (00:00)';

  let dateToUse: Date;
  switch (order.status) {
    case OrderStatus.Created:
    case OrderStatus.Pending:
    case OrderStatus.Preparing:
    case OrderStatus.Completed:
    case OrderStatus.Cancelled:
      dateToUse = new Date(order.createdAt);
      break;
    default:
      dateToUse = new Date(order.createdAt);
  }

  const now = new Date();
  const diff = Math.abs(now.getTime() - dateToUse.getTime());
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const getCreationHours = new Date(order.createdAt).getHours();
  const getCreationMinutes = new Date(order.createdAt).getMinutes();
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} (${getCreationHours.toString().padStart(2, '0')}:${getCreationMinutes.toString().padStart(2, '0')})`;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

//reload the page every 30 seconds
setInterval(() => {
  window.location.reload();
}, 15000);
</script>

<template>
    <div>
      <div class="queue-wrapper">
        <CyberLoading :is-loading="isLoading" />
        <StellarBackground backColor="#2d3434" :circle-offset="{ x: -350, y: 150 }" />
        
        <!-- Modal -->
        <div v-if="isModalOpen && checkAuth" class="modal-overlay" @click="closeModal">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h3>Order #{{ selectedOrder?.orderNumber }}</h3>
              <button class="close-button" @click="closeModal">&times;</button>
            </div>
            <div class="modal-body" v-if="selectedOrder">
              <div class="order-actions">
                <template v-if="selectedOrder.status === OrderStatus.Pending">
                  <button @click="updateOrderStatus(selectedOrder, OrderStatus.Preparing); closeModal()" 
                          class="action-button accept">
                    Accept Order
                  </button>
                </template>

                <template v-if="selectedOrder.status === OrderStatus.Preparing">
                  <button @click="updateOrderStatus(selectedOrder, OrderStatus.Completed)" 
                          class="action-button complete">
                    Complete Order
                  </button>
                  <button @click="toggleBeveragesCompleted(selectedOrder)" 
                          :class="['action-button', 'beverages', { 'beverages-completed': selectedOrder.completedBeverages }]">
                    {{ selectedOrder.completedBeverages ? 'Beverages Done' : 'Beverages Ready' }}
                  </button>
                </template>

                <template v-if="selectedOrder.status === OrderStatus.Completed">
                  <button @click="updateOrderStatus(selectedOrder, OrderStatus.Preparing); closeModal()" 
                          class="action-button cancel">
                    Undo
                  </button>
                </template>
              </div>

              <div class="order-details">
                <div class="cart-items">
                  <div v-if="sortedCartItems(selectedOrder.orderLines).beverages.length > 0" class="cart-category">
                    <h4>Beverages</h4>
                    <div v-for="item in sortedCartItems(selectedOrder.orderLines).beverages" 
                         :key="item.id" 
                         class="cart-item"
                         :class="{ 'completed-beverage': selectedOrder.completedBeverages }">
                      <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                      <span>{{ item.product.name.en }}</span>
                    </div>
                  </div>
                  <div v-if="sortedCartItems(selectedOrder.orderLines).food.length > 0" class="cart-category">
                    <h4>Food</h4>
                    <div v-for="item in sortedCartItems(selectedOrder.orderLines).food" 
                         :key="item.id" 
                         class="cart-item">
                      <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                      <span>{{ item.product.name.en }}</span>
                      <span v-if="getSupplements(item.supplements) !== ''">&nbsp;{{ getSupplements(item.supplements) }}</span>
                    </div>
                  </div>
                </div>
                <div class="order-total">
                  Total: {{ selectedOrder.totalPrice.toFixed(2) }} ALTERCOINS
                </div>
                <div v-if="selectedOrder.delivery" class="delivery-info">
                  Delivery: {{ selectedOrder.deliveryLocation }}
                </div>
                <div v-if="selectedOrder.customerNote" class="delivery-info">
                  Note: {{ selectedOrder.customerNote }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="queue-container" v-if="checkAuth">
          <div class="queue-column">
            <h2>Created</h2>
            <div class="order-list">
              <div v-for="order in orderStore.createdOrders" 
                   :key="order.id" 
                   class="order-card"
                   @click="openOrderModal(order)">
                <div class="order-header">
                  <span class="order-number">#{{ order.orderNumber }}</span>
                  <span class="order-time">{{ getWaitingTime(order) }}</span>
                </div>

                <div class="order-details">
                  <div class="cart-items">
                    <div v-if="sortedCartItems(order.orderLines).beverages.length > 0" class="cart-category">
                      <h4>Beverages</h4>
                      <div v-for="item in sortedCartItems(order.orderLines).beverages" 
                           :key="item.id" 
                           class="cart-item">
                        <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                        <span>{{ item.product.name.en }}</span>
                      </div>
                    </div>
                    <div v-if="sortedCartItems(order.orderLines).food.length > 0" class="cart-category">
                      <h4>Food</h4>
                      <div v-for="item in sortedCartItems(order.orderLines).food" 
                           :key="item.id" 
                           class="cart-item">
                        <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                        <span>{{ item.product.name.en }}</span>
                        <span v-if="getSupplements(item.supplements) !== ''">&nbsp;{{ getSupplements(item.supplements) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="order-total">
                    Total: {{ order.totalPrice.toFixed(2) }} ALTERCOINS
                  </div>
                  <div v-if="order.delivery" class="delivery-info">
                    Delivery: {{ order.deliveryLocation }}
                  </div>
                  <div v-if="order.customerNote" class="delivery-info">
                    Note: {{ order.customerNote }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="queue-column">
            <h2>Preparing</h2>
            <div class="order-list">
              <div v-for="order in orderStore.pendingOrders" 
                   :key="order.id" 
                   class="order-card"
                   @click="openOrderModal(order)">
                <div class="order-header">
                  <span class="order-number">#{{ order.orderNumber }}</span>
                  <span class="order-time">{{ getWaitingTime(order) }}</span>
                </div>

                <div class="order-details">
                  <div class="cart-items">
                    <div v-if="sortedCartItems(order.orderLines).beverages.length > 0" class="cart-category">
                      <h4>Beverages</h4>
                      <div v-for="item in sortedCartItems(order.orderLines).beverages" 
                           :key="item.id" 
                           class="cart-item"
                           :class="{ 'completed-beverage': order.completedBeverages }">
                        <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                        <span>{{ item.product.name.en }}</span>
                      </div>
                    </div>
                    <div v-if="sortedCartItems(order.orderLines).food.length > 0" class="cart-category">
                      <h4>Food</h4>
                      <div v-for="item in sortedCartItems(order.orderLines).food" 
                           :key="item.id" 
                           class="cart-item">
                        <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                        <span>{{ item.product.name.en }}</span>
                        <span v-if="getSupplements(item.supplements) !== ''">&nbsp;{{ getSupplements(item.supplements) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="order-total">
                    Total: {{ order.totalPrice.toFixed(2) }} ALTERCOINS
                  </div>
                  <div v-if="order.delivery" class="delivery-info">
                    Delivery: {{ order.deliveryLocation }}
                  </div>
                  <div v-if="order.customerNote" class="delivery-info">
                    Note: {{ order.customerNote }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="queue-column">
            <h2>Completed</h2>
            <div class="order-list">
              <div v-for="order in orderStore.completedOrders" 
                   :key="order.id" 
                   class="order-card"
                   @click="openOrderModal(order)">
                <div class="order-header">
                  <span class="order-number">#{{ order.orderNumber }}</span>
                  <span class="order-time">{{ getWaitingTime(order) }}</span>
                </div>

                <div class="order-details">
                  <div class="cart-items">
                    <div v-if="sortedCartItems(order.orderLines).beverages.length > 0" class="cart-category">
                      <h4>Beverages</h4>
                      <div v-for="item in sortedCartItems(order.orderLines).beverages" 
                           :key="item.id" 
                           class="cart-item">
                        <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                        <span>{{ item.product.name.en }}</span>
                      </div>
                    </div>
                    <div v-if="sortedCartItems(order.orderLines).food.length > 0" class="cart-category">
                      <h4>Food</h4>
                      <div v-for="item in sortedCartItems(order.orderLines).food" 
                           :key="item.id" 
                           class="cart-item">
                        <span>{{ item.quantity }}&nbsp;x&nbsp;  </span>
                        <span>{{ item.product.name.en }}</span>
                        <span v-if="getSupplements(item.supplements) !== ''">&nbsp;{{ getSupplements(item.supplements) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="order-total">
                    Total: {{ order.totalPrice.toFixed(2) }} ALTERCOINS
                  </div>
                  <div v-if="order.delivery" class="delivery-info">
                    Delivery: {{ order.deliveryLocation }}
                  </div>
                  <div v-if="order.customerNote" class="delivery-info">
                    Note: {{ order.customerNote }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<style scoped>
.queue-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh);
  width: 100vw;
  overflow: hidden;
}

.queue-header {
  text-align: center;
  position: relative;
  margin-bottom: 2rem;
  margin-top: 2rem;
  z-index: 5;
}

.queue-title {
  font-family: 'Airborne GP', sans-serif;
  font-size: 3.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: #76c4ae;
  letter-spacing: 4px;
  position: relative;
  display: inline-block;
}

.queue-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  padding: 1rem;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  height: calc(100vh);
  overflow: hidden;
  z-index: 5;
}

.queue-column {
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: scroll;
}

.queue-column h2 {
  color: #df824d;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #df824d;
}

.order-list {
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.order-card {
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #76c4ae;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #76c4ae;
  font-weight: bold;
}

.order-time {
  color: #df824d;
  font-size: 0.9rem;
}

.order-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cart-items {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.cart-category h4 {
  color: #df824d;
  font-size: 0.9rem;
  margin: 0rem 0;
}

.cart-item {
  display: flex;
  justify-content: start;
  color: #fff;
  font-size: 0.9rem;
}

.order-total {
  color: #76c4ae;
  font-weight: bold;
  margin-top: 0.5rem;
}

.delivery-info {
  color: #ff0000;
  font-size: 0.9rem;
  font-style: italic;
}

.order-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.action-button {
  flex: 1;
  padding: 0.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.3s ease;
}

.action-button:hover {
  opacity: 0.9;
}

.accept {
  background-color: #76c4ae;
  color: white;
}

.complete {
  background-color: #4CAF50;
  color: white;
}

.cancel {
  background-color: #df824d;
  color: white;
}

.beverages {
  background-color: #2196F3;
  color: white;
}

.beverages-completed {
  background-color: #4CAF50;
}

.completed-beverage {
  color: #4CAF50 !important;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid #76c4ae;
  border-radius: 12px;
  padding: 1.5rem;
  width: 90%;
  max-width: 500px;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.modal-header h3 {
  color: #76c4ae;
  font-size: 1.5rem;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  color: #df824d;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.3s ease;
}

.close-button:hover {
  color: #ff9b60;
}

.modal-body {
  padding: 1rem 0;
}

@media (max-width: 1024px) {
  .queue-container {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
  }

  .queue-column {
    height: auto;
    max-height: none;
  }

  .order-list {
    max-height: none;
  }

  .queue-title {
    font-size: 2rem;
  }
}
</style>
